import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArcLanes } from '../board/ArcLanes';
import { useListDrag } from '../plan/useBoardDrag';
import type { DropTarget, ListDrag } from '../plan/useBoardDrag';
import { capabilitiesOf } from '../entity/capabilities';
import type { Capabilities } from '../entity/capabilities';
import {
  bodyWithoutRefs,
  capturedAt,
  flat,
  isUntitled,
  kindOf,
  LISTED_ROLES,
  listOf,
  memberRefs,
  picksIn,
  refNames,
  sectionNamed,
  SOURCE_ROLE,
  TALK_ROLE,
  transcriptTopics,
} from '../entity/entity';
import type { MemberRef, Pick, Topic } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { renderMarkdown } from '../plan/markdown';
import { tagTint } from '../plan/registries';
import type { Registries } from '../plan/registries';
import { chipTint, markInk } from '../design/tokens';
import type { Theme } from '../design/tokens';
import { StatusGlyph } from '../board/StatusGlyph';
import { FolderLink } from '../folders/FolderChip';
import { folderMark, TagMarkGlyph } from '../folders/TagMark';
import { StatusPicker } from '../board/StatusPicker';
import { parseArc, statusVar, STATUSES } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';
import { editableText } from '../writing/textNote';
import { mergedPicks } from '../entity/face';
import { EditableName } from '../design/EditableName';
import { VaultAudio } from '../doc/VaultAudio';
import { BAND_ORDER, bandFor } from '../entity/bands';
import { kindByFolder } from '../vault/kinds';
import '../entity/face.css';

/**
 * Arcs are the set the board already holds, so they keep the route that
 * renders them with tag tints and title-resolved wiki links. Everything else
 * is read one file at a time.
 */
/** The faces of an entity page that live at a path of their own. */
export type EntityMode = 'log' | 'lanes';

/** An arc kept outside `arcs/` gets the file route, which hands it on to its
 *  arc route once the plan says it is one. */
export function hrefFor(path: string): string {
  return kindByFolder(path) === 'arc'
    ? `/arc/${encodeURIComponent(path.replace(/^.*\//, ''))}`
    : `/file/${encodeURIComponent(path)}`;
}

export function entityHref(name: string): string {
  return hrefFor(`arcs/${name}`);
}

/**
 * Everything the file holds, on one page: the prose you write on the left,
 * the reference material you consult on the right. Nothing behind a second
 * click except the machine state, which the browser never touches.
 *
 * Blocks are chosen by [capabilitiesOf], never by asking what kind of thing
 * this is — an arc and a recording differ in which blocks appear, not in
 * which component renders them.
 */
export function EntityPage({
  entity,
  registries,
  theme,
  resolveName,
  titleOf,
  onAppend,
  onSetStatus,
  onSetArchived,
  onRename,
  onReorderHeld,
  kindAt,
  onSetFace,
  inArcs,
  mode = 'log',
  members,
  onMoveMember,
  onSetMemberStatus,
  onSetMemberArchived,
}: {
  entity: VaultEntity;
  registries: Registries;
  theme: Theme;
  resolveName: (name: string) => VaultEntity | null;
  titleOf: (title: string) => VaultEntity | null;
  /** The arcs this storyline holds, resolved. Absent on anything that holds
   *  none — which is what decides whether lanes are offered at all. */
  members?: Arc[];
  /** Absent where the surface cannot write: without it lanes would be a mode
   *  you can enter and not use, so it is not offered. */
  onMoveMember?: (name: string, target: DropTarget) => void;
  /** The status button on a member's card — the same write the drag makes,
   *  for when the point is the state rather than the position. */
  onSetMemberStatus?: (arc: Arc, status: Status, evidence?: string) => void;
  /** Filing a member away from its holder's lanes, beside [onSetMemberStatus]. */
  onSetMemberArchived?: (arc: Arc, archived: boolean) => void;
  /** Absent where the surface cannot write — a read-only file has no write
   *  line rather than a disabled one. */
  onAppend?: (text: string) => Promise<void>;
  /** Absent where the surface cannot write, like [onAppend]. Present, the
   *  status chip is the control it already looks like. */
  onSetStatus?: (status: Status, evidence?: string) => void;
  /** Filing this entity away, from the same chip that sets its status —
   *  `visibility:` is a different field, so it is a different write. */
  onSetArchived?: (archived: boolean) => void;
  /** Renaming it, from its title. Absent where the name cannot travel back to
   *  the app from here — the title is then only text. */
  onRename?: (name: string) => void;
  /** Absent where the surface cannot write: the face still expands to show
   *  every candidate, it just cannot be changed from there. */
  onSetFace?: (picks: Pick[]) => void;
  /** Reordering the arcs a storyline or workspace holds, from the holds panel.
   *  Takes the file that OWNS the list, which is not always this page's — a
   *  storyline expanded in place inside it owns its own. */
  onReorderHeld?: (target: VaultEntity, order: string[]) => void;
  /** What the catalog calls the file at a path — `recording`, `note`, … Held
   *  material is banded by it, the way the details screen splits Recordings
   *  from Notes. Absent where nothing knows what a path holds: one band. */
  kindAt?: (path: string) => string | null;
  /** Null while still being worked out — the section says so rather than
   *  claiming the answer is none. */
  inArcs?: Arc[] | null;
  /** Which face of the page the route is standing on. `plane` is its own
   *  route and never arrives here. */
  mode?: EntityMode;
}) {
  const can = capabilitiesOf(entity);
  // Offered only where there is something to arrange and somewhere to write
  // it. A storyline holding two arcs reads fine as a list.
  const canLanes = Boolean(onMoveMember && onSetMemberStatus && members && members.length > 0);
  // A mode the URL does not offer is one nobody can be standing in — landing on
  // /lanes for an arc that holds nothing falls back rather than showing five
  // empty columns.
  const inLanes = canLanes && mode === 'lanes';

  if (can.tombstone) return <Tombstone entity={entity} />;

  return (
    <div className="page">
      <Identity
        entity={entity}
        can={can}
        registries={registries}
        theme={theme}
        onSetStatus={onSetStatus}
        onSetArchived={onSetArchived}
        onRename={onRename}
        mode={inLanes ? 'lanes' : 'log'}
        lanes={canLanes}
      />

      {inLanes && members && onMoveMember && onSetMemberStatus ? (
        // The header stays and everything below it goes: in this mode the page
        // is about where the members stand, and the holder's own log and its
        // flat list of what it holds are both answering a different question.
        <ArcLanes
          members={members}
          registries={registries}
          theme={theme}
          onMove={onMoveMember}
          onSetStatus={onSetMemberStatus}
          onSetArchived={onSetMemberArchived}
        />
      ) : (
      <div className="page-cols">
        <div className="page-log">
          {(can.hasFace || can.hasSuggestions) && <Face entity={entity} onSetFace={onSetFace} />}
          {can.hasTranscript ? (
            <Transcript entity={entity} />
          ) : (
            <div className="prose t-bodyProse">
              {/* A workspace's sources and talk are listed in the rail, where
                  they open; read out here they would be the same list, unlinked. */}
              {renderMarkdown(bodyWithoutRefs(entity, LISTED_ROLES), {
                resolveArc: (title) => {
                  const target = titleOf(title);
                  return target ? entityHref(target.name) : null;
                },
              })}
            </div>
          )}
          {onAppend && !can.hasTranscript && <WriteLine onAppend={onAppend} />}
        </div>

        <aside className="page-rail">
          {(can.holdsMembers || can.planned) && (
            <Members
              entity={entity}
              resolveName={resolveName}
              planned={can.planned}
              onReorderHeld={onReorderHeld}
              kindAt={kindAt}
            />
          )}
          {(can.hasTranscript || can.planned) && (
            <InArcs
              arcs={inArcs}
              // An arc's holder is a storyline, and "not gathered into an arc"
              // would read as a recording's answer on a page that is not one.
              empty={can.planned ? 'not in a storyline' : 'not gathered into an arc'}
            />
          )}
          {can.hasTranscript && <TopicIndex entity={entity} />}
          <Context entity={entity} registries={registries} theme={theme} />
          <FileFacts entity={entity} can={can} />
        </aside>
      </div>
      )}
    </div>
  );
}

/**
 * The one place prose fields are LABELLED — here they compete, so they are
 * named. On a board face a single line cannot be confused with anything, and
 * a label there would be noise.
 */
function Identity({
  entity,
  can,
  registries,
  theme,
  onSetStatus,
  onSetArchived,
  onRename,
  mode,
  lanes,
}: {
  entity: VaultEntity;
  can: Capabilities;
  registries: Registries;
  theme: Theme;
  onSetStatus?: (status: Status, evidence?: string) => void;
  onSetArchived?: (archived: boolean) => void;
  onRename?: (name: string) => void;
  mode: EntityMode;
  /** Whether lanes are on offer at all — see [EntityPage]. */
  lanes: boolean;
}) {
  const status = flat(entity.frontMatter['status']) as Status | '';
  const sourceCount = listOf(entity.frontMatter['sources']).length;
  const captured = capturedAt(entity);
  const topics = can.hasTranscript ? transcriptTopics(entity).length : 0;
  const face = picksIn(sectionNamed(entity, 'face')?.body ?? '').length;
  const untitled = can.hasTranscript && isUntitled(entity);

  // The node's own state (`visibility:` front matter) — a done or archived
  // thing opened directly should say so, not read as current material.
  const visibility = flat(entity.frontMatter['visibility']);

  const eyebrow = (
    can.hasTranscript
      ? [
          kindOf(entity),
          visibility,
          topics ? `${topics} topics` : '',
          face ? `${face} on the face` : '',
          captured ? `${captured.day}` : '',
          captured ? captured.time : '',
        ]
      : [
          can.planned ? 'arc' : kindOf(entity),
          visibility,
          flat(entity.frontMatter['area']),
          sourceCount ? `${sourceCount} sources` : '',
          entity.frontMatter['updated'] ? `updated ${flat(entity.frontMatter['updated'])}` : '',
        ]
  ).filter(Boolean);

  return (
    <header className="identity">
      <div className="identity-top">
        <span className="t-eyebrowMono identity-eyebrow">
          {status && <StatusGlyph status={status as Status} />}
          {eyebrow.join(' · ')}
        </span>
        <ModeSwitch entity={entity} can={can} mode={mode} lanes={lanes} />
        {/* The way back from the detail view to the text, which is what makes
            the two a switch rather than a one-way trip. Absent where the file
            holds more than one message — see [editableText]. */}
        {can.writable && !can.planned && editableText(entity.raw) !== null && (
          <Link className="mode-switch t-chipLabel" to={`/write/${encodeURIComponent(entity.path)}`}>
            text
          </Link>
        )}
      </div>

      {/* The state belongs to the ARC and the switcher belongs to the VIEW.
          Sitting in one group they read as the same kind of control, so the
          chip closes the title line — which is where this entity says what it
          is — and the modes stay up in the eyebrow row with the other chrome. */}
      <div className="identity-title">
        {onRename && !untitled ? (
          <h1 className="t-pageTitle">
            <EditableName name={entity.title} onRename={onRename} />
          </h1>
        ) : (
          <h1 className="t-pageTitle">
            {untitled ? <span className="untitled">untitled</span> : entity.title}
          </h1>
        )}
        {status && (
          <StatusChip
            status={status as Status}
            arc={onSetStatus ? parseArc(entity.path, entity.raw) : null}
            onPick={onSetStatus}
            onArchive={onSetArchived}
          />
        )}
      </div>

      {can.hasTranscript && <VaultAudio notePath={entity.path} size={56} fallback={<Player />} />}

      {/* Above `ends when`, because they answer different questions and only
          one of them is live: the criterion stands whether or not anything is
          waiting, and this is the part currently sitting with the reader. Same
          rule as the row's face — the ask outranks what an arc would otherwise
          say about itself.

          Rendered only at `needs-input`. A `needs:` line left behind by a
          status that moved on is stale by definition, and showing it would put
          an answered question back in front of the person who answered it. */}
      {status === 'needs-input' && entity.frontMatter['needs'] && (
        <div className="ends-when is-asking">
          <span className="ends-when-bar" />
          <div>
            <div className="t-eyebrowMono ends-when-label">needs you</div>
            <p className="t-bodyProse t-wide">{flat(entity.frontMatter['needs'])}</p>
          </div>
        </div>
      )}

      {/* The way in, on every status: this page is where an arc is picked up
          again, parked or not. Before the criterion — how to begin, then how it
          ends. */}
      {entity.frontMatter['start-with'] && (
        <div className="ends-when is-starting">
          <span className="ends-when-bar" />
          <div>
            <div className="t-eyebrowMono ends-when-label">start with</div>
            <p className="t-bodyProse t-wide">{flat(entity.frontMatter['start-with'])}</p>
          </div>
        </div>
      )}

      {entity.frontMatter['done-when'] && (
        <div className="ends-when">
          <span className="ends-when-bar" />
          <div>
            <div className="t-eyebrowMono ends-when-label">ends when</div>
            <p className="t-bodyProse t-wide">{flat(entity.frontMatter['done-when'])}</p>
          </div>
        </div>
      )}

      {entity.frontMatter['scope'] && (
        <div className="scope">
          <div className="t-eyebrowMono scope-label">scope</div>
          <p className="t-bodyProse t-wide">{flat(entity.frontMatter['scope'])}</p>
        </div>
      )}

      <Chips entity={entity} registries={registries} theme={theme} />
    </header>
  );
}

/**
 * Audio is referenced but not synced yet, and this is a STATE of a normal
 * recording rather than a different kind of item.
 *
 * So the control renders at full size and says plainly why it cannot play,
 * instead of being hidden — a recording whose bytes are elsewhere must not
 * quietly read as a text note. When the media plane lands, the dashed ring
 * becomes a play button and nothing else on the page changes.
 */
function Player() {
  return (
    <div className="player">
      <span className="player-ring" aria-hidden="true" />
      <div>
        <p className="t-metaMono player-note">audio is still on your phone — it has not reached the vault</p>
      </div>
    </div>
  );
}

function Tombstone({ entity }: { entity: VaultEntity }) {
  return (
    <div className="page">
      <header className="identity">
        <span className="t-eyebrowMono identity-eyebrow">deleted</span>
        <h1 className="t-pageTitle">This was deleted</h1>
        <p className="t-bodyProse scope">
          A marker stays in the vault so devices that sync later learn about the
          deletion. There is nothing left to read.
        </p>
        <p className="t-metaMono file-locked">{entity.path}</p>
      </header>
    </div>
  );
}

/**
 * The status is editable where it is the subject and the surface can write —
 * the same picker the board opens, so `done` asks for its evidence here too
 * rather than writing a file the plan would refuse.
 */
/**
 * The faces of this entity, as one connected control rather than a scatter of
 * separate affordances.
 *
 * Every face is a ROUTE, which is what lets this be a set of links: the mode
 * survives a re-read, a reload and the back button, none of which it did while
 * it was a toggle held in state. Segments appear only where the entity can
 * actually stand in them, and a control with one segment is not a choice — it
 * renders as nothing.
 */
function ModeSwitch({
  entity,
  can,
  mode,
  lanes,
}: {
  entity: VaultEntity;
  can: Capabilities;
  mode: EntityMode;
  lanes: boolean;
}) {
  const base = hrefFor(entity.path);
  const faces = [
    { key: 'log', label: 'details', to: base, on: mode === 'log', show: true },
    { key: 'lanes', label: 'lanes', to: `${base}/lanes`, on: mode === 'lanes', show: lanes },
    // The plane is its own route and never reports back here, so it is never
    // the current one from this component's point of view.
    { key: 'plane', label: 'plane', to: `${base}/plane`, on: false, show: can.planned },
  ].filter((face) => face.show);

  if (faces.length < 2) return null;

  return (
    <span className="mode-seg t-chipLabel">
      {faces.map((face) => (
        <Link
          key={face.key}
          to={face.to}
          className={face.on ? 'is-on' : undefined}
          aria-current={face.on ? 'page' : undefined}
        >
          {face.label}
        </Link>
      ))}
    </span>
  );
}

function StatusChip({
  status,
  arc,
  onPick,
  onArchive,
}: {
  status: Status;
  arc: Arc | null;
  onPick?: (status: Status, evidence?: string) => void;
  onArchive?: (archived: boolean) => void;
}) {
  const [open, setOpen] = useState(false);

  if (arc && onPick) {
    return (
      <span className="status-chip-holder">
        <StatusChipFace status={status} onClick={() => setOpen((o) => !o)} />
        {open && (
          <StatusPicker
            arc={arc}
            onClose={() => setOpen(false)}
            onPick={(next, evidence) => {
              onPick(next, evidence);
              setOpen(false);
            }}
            onArchive={
              onArchive &&
              ((archived) => {
                onArchive(archived);
                setOpen(false);
              })
            }
          />
        )}
      </span>
    );
  }

  return <StatusChipFace status={status} />;
}

function StatusChipFace({ status, onClick }: { status: Status; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={`status-chip t-chipLabel${onClick ? ' is-button' : ''}`}
      onClick={onClick}
      title={onClick ? 'Change status' : undefined}
      style={{
        background: `color-mix(in srgb, var(${statusVar(status)}) 13%, transparent)`,
        borderColor: `color-mix(in srgb, var(${statusVar(status)}) 32%, transparent)`,
      }}
    >
      <StatusGlyph status={status} />
      {status}
    </Tag>
  );
}

/**
 * Always present as the last thing you can write into. Not a mode, not behind
 * a button — the cost of writing is stated once, here, where it happens.
 */
function WriteLine({ onAppend }: { onAppend: (text: string) => Promise<void> }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const commit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await onAppend(text);
      setText('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`writeline${text ? ' has-draft' : ''}`}>
      <textarea
        className="t-bodyProse"
        value={text}
        placeholder="Add to the log…"
        rows={1}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void commit();
          }
        }}
      />
      {text && (
        <p className="t-metaMono writeline-hint">
          ⌘↵ commits — the file re-encrypts once and reaches the phone in seconds
        </p>
      )}
    </div>
  );
}

function Members({
  entity,
  resolveName,
  planned,
  onReorderHeld,
  kindAt,
}: {
  entity: VaultEntity;
  resolveName: (name: string) => VaultEntity | null;
  planned: boolean;
  onReorderHeld?: (target: VaultEntity, order: string[]) => void;
  kindAt?: (path: string) => string | null;
}) {
  const held = refNames(entity.frontMatter['arcs']);
  const cited = refNames(entity.frontMatter['sources']);
  const refs = memberRefs(entity.raw);
  const sources = refs.filter((ref) => ref.role === SOURCE_ROLE);
  const talk = refs.find((ref) => ref.role === TALK_ROLE);

  // Never hidden on an arc. An absent section reads as a missing feature;
  // an empty one reads as an arc nobody has gathered into yet.
  if (planned && held.length === 0 && cited.length === 0) {
    return (
      <RailSection title="from">
        <p className="t-memberTitle rail-empty">nothing gathered into this arc yet</p>
      </RailSection>
    );
  }

  return (
    <>
      {held.length > 0 && (
        <Holds
          entity={entity}
          names={held}
          resolveName={resolveName}
          onReorder={onReorderHeld}
        />
      )}
      {cited.length > 0 && (
        // `sources:` is where this came from, not what it groups — the
        // distinction the board depends on, so the page states it too.
        <RailSection title="from">
          <RefList names={cited} resolveName={resolveName} />
        </RailSection>
      )}
      {sources.length > 0 && (
        <Sources refs={sources} resolveName={resolveName} kindAt={kindAt} />
      )}
      {talk && (
        <RailSection title="talk">
          <RefList names={[fileName(talk.path)]} resolveName={resolveName} />
        </RailSection>
      )}
    </>
  );
}

interface HeldFile {
  name: string;
  /** How many of this file's topics are held; 0 when it is held whole. */
  topics: number;
  band: string;
}

/**
 * One row per file, in the order the workspace lists them. Topics held from
 * one recording are counted on its row rather than listed as copies of its
 * name: a topic's title lives inside the recording, and reading every held
 * file to find it would defeat reading this page as one file.
 */
function heldFiles(refs: MemberRef[], kindAt?: (path: string) => string | null): HeldFile[] {
  const out = new Map<string, HeldFile>();
  for (const ref of refs) {
    const name = fileName(ref.path);
    const seen = out.get(name);
    if (seen) {
      if (ref.anchor) seen.topics += 1;
      continue;
    }
    out.set(name, {
      name,
      topics: ref.anchor ? 1 : 0,
      band: bandFor(kindAt?.(ref.path) ?? null, ref.path),
    });
  }
  return [...out.values()];
}

function Sources({
  refs,
  resolveName,
  kindAt,
}: {
  refs: MemberRef[];
  resolveName: (name: string) => VaultEntity | null;
  kindAt?: (path: string) => string | null;
}) {
  const files = heldFiles(refs, kindAt);
  const bands = BAND_ORDER.filter((band) => files.some((file) => file.band === band));

  return (
    <>
      {bands.map((band) => (
        <RailSection key={band} title={band}>
          <RefList
            names={files.filter((file) => file.band === band).map((file) => file.name)}
            resolveName={resolveName}
            noteOf={(name) => {
              const n = files.find((file) => file.name === name)?.topics ?? 0;
              return n > 0 ? `${n} ${n === 1 ? 'topic' : 'topics'}` : null;
            }}
          />
        </RailSection>
      ))}
    </>
  );
}

function fileName(path: string): string {
  return path.replace(/^.*\//, '');
}

function RailSection({
  title,
  actions,
  children,
}: {
  title: string;
  /** Controls belonging to the region, on the head's own line. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  // A categorical head labels a region and carries no rule; the region's own
  // boundary already divides.
  return (
    <section className="rail-section">
      <div className="rail-head-row">
        <h2 className="t-eyebrowMono rail-head">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * What a storyline holds — in its own order first, and by state after that.
 *
 * **`all` leads, and is the default.** It is the arcs in the order the file
 * lists them, which IS the member order the app reads back, so this band is
 * the plan: what to do, in the order it is to be done. The state bands are a
 * reading OF that plan and stay behind it — useful when a long-running
 * storyline holds mostly finished work and you want the live part, but they
 * are an answer to a narrower question than the one the panel is usually
 * opened with.
 *
 * The bands are exclusive and you switch between them.
 *
 * `next` gets a band of its own rather than joining the first: [IN_FLIGHT] here
 * means being worked or waiting on you, deliberately NOT merely queued, and
 * quietly widening it in one surface is how the two heads come to disagree about
 * what counts as live.
 *
 * Chips appear only when more than one band has anything in it — with a single
 * band there is no choice to make, and a control offering one option is noise.
 */
const BANDS = ['all', 'in flight', 'next', 'parked', 'done'] as const;
type Band = (typeof BANDS)[number];

function bandOf(status: Status | null): Band {
  if (status === 'parked') return 'parked';
  if (status === 'done') return 'done';
  if (status === 'next') return 'next';
  // Anything unstated counts as live: an absent status is not a settled one.
  return 'in flight';
}

/**
 * [withAll] is what makes a list a PLAN rather than a lookup. `holds` is
 * ordered — the order is the member order — so it leads with the whole of it.
 * `in arcs` answers "what am I inside", where the set has no order of its own
 * and an `all` band would only be a longer way to say the same thing.
 */
function bandsOf<T>(
  items: T[],
  statusOf: (item: T) => Status | null,
  withAll = false,
) {
  const byBand = new Map<Band, T[]>();
  for (const item of items) {
    const band = bandOf(statusOf(item));
    byBand.set(band, [...(byBand.get(band) ?? []), item]);
  }
  if (!withAll) return { byBand, present: BANDS.filter((band) => byBand.has(band)) };

  // Everything, in the order given — not derived from a status, so not one of
  // the buckets the loop fills.
  const all = new Map<Band, T[]>([['all', items], ...byBand]);
  // With the members all in one state that band holds exactly what `all`
  // holds, so offering both is two chips for one list — the noise the
  // one-band rule already forbids, arriving from the other direction. `all`
  // is the one kept, deliberately: it is the band the order is edited in, and
  // dropping it instead would make a storyline whose work is all live —
  // precisely the one worth putting in order — the one that cannot be.
  const states = BANDS.filter((band) => band !== 'all' && byBand.has(band));
  return {
    byBand: all,
    present: states.length > 1 ? (['all', ...states] as Band[]) : (['all'] as Band[]),
  };
}

function BandChips<T>({
  present,
  shown,
  byBand,
  onPick,
}: {
  present: readonly Band[];
  shown: Band;
  byBand: Map<Band, T[]>;
  onPick: (band: Band) => void;
}) {
  if (present.length <= 1) return null;
  return (
    <span className="rail-seg t-chipLabel">
      {present.map((band) => (
        <button
          key={band}
          type="button"
          className={band === shown ? 'is-on' : undefined}
          onClick={() => onPick(band)}
        >
          {band}
          <span className="rail-seg-count">{byBand.get(band)!.length}</span>
        </button>
      ))}
    </span>
  );
}

/**
 * The plan a storyline — or a workspace — holds, reorderable where it is read.
 *
 * The order written here is the MEMBER order: the app flows a changed `arcs:`
 * list straight onto the member edges' positions, and re-derives the list from
 * those positions on the way out. So a drag is a real plan edit that reaches
 * every device, not a view preference this surface keeps to itself. A workspace
 * carries the same `arcs:` key for the same reason, which is why nothing here
 * asks what kind of file it is on.
 *
 * Reordering is offered in `all` and nowhere else. A band shows a SUBSET, so a
 * drop inside one says only "after this one of the four still shown" — the
 * position among everything it is actually stored beside is unstated, and any
 * order written from it would be a guess about the rows the band is hiding.
 */
function Holds({
  entity,
  names,
  resolveName,
  onReorder,
}: {
  entity: VaultEntity;
  names: string[];
  resolveName: (name: string) => VaultEntity | null;
  onReorder?: (target: VaultEntity, order: string[]) => void;
}) {
  const { byBand, present } = bandsOf(
    names,
    (name) => {
      const target = resolveName(name);
      return target ? statusOf(target) : null;
    },
    true,
  );
  const [picked, setPicked] = useState<Band | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // Both callers render nothing when the list is empty, so the fallback is
  // unreachable rather than a default anybody sees.
  const shown = picked && byBand.has(picked) ? picked : (present[0] ?? 'all');

  /** Every list on screen, by the path of the file that owns it — the outer
   *  one and any storyline expanded inside it. */
  const lists = new Map<string, { entity: VaultEntity; names: string[] }>();
  lists.set(entity.path, { entity, names });
  for (const name of names) {
    const target = open.has(name) ? resolveName(name) : null;
    const held = target ? refNames(target.frontMatter['arcs']) : [];
    if (target && held.length > 0) lists.set(target.path, { entity: target, names: held });
  }

  const drag = useListDrag((list, name, beforeName) => {
    const owner = lists.get(list);
    if (!owner || !onReorder) return;
    const rest = owner.names.filter((n) => n !== name);
    const at = beforeName ? rest.indexOf(beforeName) : rest.length;
    rest.splice(at < 0 ? rest.length : at, 0, name);
    onReorder(owner.entity, rest);
  });

  const reorderable = Boolean(onReorder) && shown === 'all';

  return (
    <RailSection
      title="holds"
      actions={<BandChips present={present} shown={shown} byBand={byBand} onPick={setPicked} />}
    >
      <div
        onPointerMove={reorderable ? drag.onListPointerMove : undefined}
        onPointerUp={reorderable ? drag.onListPointerUp : undefined}
        onPointerCancel={reorderable ? drag.onListPointerUp : undefined}
      >
        <RefList
          listPath={entity.path}
          names={byBand.get(shown) ?? []}
          resolveName={resolveName}
          drag={reorderable ? drag : undefined}
          open={reorderable ? open : undefined}
          onToggleOpen={
            reorderable
              ? (name) =>
                  setOpen((current) => {
                    const next = new Set(current);
                    if (!next.delete(name)) next.add(name);
                    return next;
                  })
              : undefined
          }
        />
      </div>
    </RailSection>
  );
}

/**
 * A list of referenced entities. Plain by default — `from` and the bands use
 * it that way — and reorderable when [drag] is handed over.
 *
 * [listPath] is the file whose list this is, which the drag reads off the DOM
 * to know where a row may land. It matters because these NEST: a storyline
 * held by a workspace opens in place, and its members are a list of their own
 * belonging to a different file.
 */
function RefList({
  names,
  resolveName,
  noteOf,
  listPath,
  drag,
  open,
  onToggleOpen,
  depth = 0,
}: {
  names: string[];
  resolveName: (name: string) => VaultEntity | null;
  /** A word beside a row's title — how many of a file's topics are held. */
  noteOf?: (name: string) => string | null;
  listPath?: string;
  drag?: ListDrag;
  open?: ReadonlySet<string>;
  onToggleOpen?: (name: string) => void;
  depth?: number;
}) {
  return (
    <ul className="rail-list" data-hold-list={listPath}>
      {names.map((name) => {
        const target = resolveName(name);
        const held = target ? refNames(target.frontMatter['arcs']) : [];
        const isOpen = open?.has(name) ?? false;
        const dragging = drag?.draggingName === name;
        const dropBefore = drag?.drop?.list === listPath && drag?.drop?.beforeName === name;
        const note = noteOf?.(name) ?? null;

        return (
          <li
            key={name}
            className={
              `t-memberTitle${drag ? ' is-movable' : ''}${dragging ? ' dragging' : ''}` +
              `${dropBefore ? ' drop-before' : ''}`
            }
            data-arc-name={drag ? name : undefined}
            onPointerDown={drag?.onRowPointerDown(name)}
          >
            {target ? (
              <span className="ref-row">
                {drag && (
                  <span className="rail-grip" aria-hidden="true">
                    ⠿
                  </span>
                )}
                {/* A held storyline opens where it is rather than sending you
                    to its own page: the point of the panel is that the plan
                    can be reordered without leaving what you are looking at. */}
                {onToggleOpen && held.length > 0 ? (
                  <button
                    type="button"
                    className="rail-open"
                    aria-expanded={isOpen}
                    title={`${held.length} inside`}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => onToggleOpen(name)}
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>
                ) : null}
                <Link to={hrefFor(target.path)} className="ref-row">
                  {/* An arc says what state it is in, here as everywhere. A
                      recording has none and gets no placeholder — an empty
                      glyph slot would read as a status nobody set. */}
                  {statusOf(target) && <StatusGlyph status={statusOf(target)!} />}
                  <span>{target.title}</span>
                </Link>
                {note && <span className="t-metaMono band-count">{note}</span>}
              </span>
            ) : (
              <span className="wiki-unresolved">{name.replace(/\.md$/, '')}</span>
            )}

            {isOpen && target && held.length > 0 && (
              <RefList
                names={held}
                resolveName={resolveName}
                listPath={target.path}
                drag={drag}
                depth={depth + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The picked memory triggers, directly under the identity and above the
 * transcript. It is the recognition device — what brings a recording back at
 * a glance — so it is not behind a tab.
 *
 * The face and the suggestions are ONE list at two densities, not two
 * columns. They were rendered as unrelated blocks — the face here, the
 * candidates in the rail — though the relationship is already true in the
 * data: the face is the picked subset of the suggestions. So the resting
 * state is unchanged, and expanding shows every candidate in place with the
 * picked ones marked, which is also where membership is changed.
 */
function Face({ entity, onSetFace }: { entity: VaultEntity; onSetFace?: (picks: Pick[]) => void }) {
  const picks = picksIn(sectionNamed(entity, 'face')?.body ?? '');
  const suggestions = picksIn(sectionNamed(entity, 'suggestions')?.body ?? '');
  /**
   * The rows the expanded list shows, fixed when it opens.
   *
   * Recomputing per render would make a hand-picked line — one on the face
   * that was never suggested — vanish the moment it is toggled off, since
   * neither section would list it any more. Holding the roster keeps it there
   * to be toggled back on.
   */
  const [roster, setRoster] = useState<Pick[] | null>(null);
  const onFace = new Set(picks.map((p) => p.ref));

  const toggle = (pick: Pick) => {
    if (!onSetFace) return;
    onSetFace(
      onFace.has(pick.ref) ? picks.filter((p) => p.ref !== pick.ref) : [...picks, pick],
    );
  };

  return (
    <section className="face">
      <h2 className="t-eyebrowMono face-head">
        face <span className="face-count">{picks.length} memory triggers</span>
        {suggestions.length > 0 && (
          <button
            type="button"
            className="rail-toggle t-metaMono face-expand"
            onClick={() => setRoster(roster ? null : mergedPicks(picks, suggestions))}
          >
            {roster ? 'done' : `all ${mergedPicks(picks, suggestions).length}`}
          </button>
        )}
      </h2>

      {(roster ?? picks).map((pick) => (
        <PickLine
          key={pick.ref}
          pick={pick}
          on={roster ? onFace.has(pick.ref) : true}
          onToggle={roster && onSetFace ? () => toggle(pick) : undefined}
        />
      ))}

      {roster && roster.length === 0 && (
        <p className="t-metaMono face-empty">nothing was suggested for this one</p>
      )}
    </section>
  );
}

/**
 * Rendered as TEXT. `pondering://cb/<id>` is the serializer's internal
 * content-block reference and navigates nothing, so a link here would be a
 * promise the product cannot keep.
 *
 * The heart is the toggle in the expanded state rather than a second control
 * beside it — whether the face should carry hearts at REST is a separate
 * question and deliberately not answered here.
 */
function PickLine({ pick, on, onToggle }: { pick: Pick; on: boolean; onToggle?: () => void }) {
  if (!onToggle) {
    return (
      <p className={`face-line${on ? '' : ' off'}`}>
        {pick.text}
        <HeartGlyph filled={on} />
      </p>
    );
  }

  return (
    <button
      type="button"
      className={`face-line face-pick${on ? '' : ' off'}`}
      aria-pressed={on}
      onClick={onToggle}
    >
      {pick.text}
      <HeartGlyph filled={on} />
    </button>
  );
}

function HeartGlyph({ filled = true }: { filled?: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true" className="heart">
      <path
        d="M7 12.2 2.3 7.6a2.9 2.9 0 0 1 4.1-4.1L7 4.1l.6-.6a2.9 2.9 0 0 1 4.1 4.1z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={filled ? 0 : 1.3}
      />
    </svg>
  );
}

function Transcript({ entity }: { entity: VaultEntity }) {
  const topics = transcriptTopics(entity);
  return (
    <section className="transcript">
      <h2 className="t-eyebrowMono rail-head">transcript</h2>
      {topics.map((topic) => (
        <TopicBlock key={topic.anchor} topic={topic} />
      ))}
    </section>
  );
}

function TopicBlock({ topic }: { topic: Topic }) {
  return (
    <section className="topic" id={topic.anchor}>
      {/* A dotted head, not solid and not mono caps: a topic is neither a
          category nor a date, and it must not compete with a section head.
          Dimmer still when the name is a stand-in for one nobody wrote. */}
      <h3 className={`topic-head${topic.unnamed ? ' unnamed' : ''}`}>{topic.title}</h3>
      {topic.paragraphs.map((p, i) => (
        <p key={i} className="t-bodyProse topic-para">
          {p}
        </p>
      ))}
    </section>
  );
}

/** Membership read from the ARCS' sources, not from anything this file
 *  records — a recording does not know what cites it, which is why this
 *  costs a read of the plan and why it arrives after the page. */
function InArcs({ arcs, empty }: { arcs: Arc[] | null | undefined; empty: string }) {
  if (arcs === null || arcs === undefined) {
    return (
      <RailSection title="in arcs">
        <p className="t-memberTitle rail-empty">looking…</p>
      </RailSection>
    );
  }
  if (arcs.length === 0) {
    return (
      <RailSection title="in arcs">
        <p className="t-memberTitle rail-empty">{empty}</p>
      </RailSection>
    );
  }
  return <InArcsBands arcs={arcs} />;
}

/**
 * The same banding `holds` uses, from the other direction.
 *
 * A recording that has been worked over sits in a run of arcs most of which are
 * finished, and the question "what still claims this" is the one being asked
 * far more often than "what ever claimed it".
 */
function InArcsBands({ arcs }: { arcs: Arc[] }) {
  const { byBand, present } = bandsOf(arcs, (arc) => arc.status);
  const [picked, setPicked] = useState<Band | null>(null);
  // Both callers render nothing when the list is empty, so the fallback is
  // unreachable rather than a default anybody sees.
  const shown = picked && byBand.has(picked) ? picked : (present[0] ?? 'in flight');

  return (
    <RailSection
      title="in arcs"
      actions={<BandChips present={present} shown={shown} byBand={byBand} onPick={setPicked} />}
    >
      <ul className="rail-list">
        {(byBand.get(shown) ?? []).map((arc) => (
          <li key={arc.path} className="t-memberTitle in-arc">
            <StatusGlyph status={arc.status} />
            <Link to={hrefFor(arc.path)}>{arc.title}</Link>
          </li>
        ))}
      </ul>
    </RailSection>
  );
}

function TopicIndex({ entity }: { entity: VaultEntity }) {
  const topics = transcriptTopics(entity);
  if (topics.length === 0) return null;
  return (
    <RailSection title="topics">
      <ul className="rail-list">
        {topics.map((topic) => (
          <li key={topic.anchor} className="t-memberTitle">
            <a href={`#${topic.anchor}`} className={topic.unnamed ? 'unnamed' : undefined}>
              {topic.title}
            </a>
          </li>
        ))}
      </ul>
    </RailSection>
  );
}

function Context({
  entity,
  registries,
  theme,
}: {
  entity: VaultEntity;
  registries: Registries;
  theme: Theme;
}) {
  const tags = listOf(entity.frontMatter['tags']);
  const markers = listOf(entity.frontMatter['markers']);
  if (!tags.length && !markers.length) return null;

  return (
    <RailSection title="context">
      <Chips entity={entity} registries={registries} theme={theme} />
    </RailSection>
  );
}

function Chips({
  entity,
  registries,
  theme,
}: {
  entity: VaultEntity;
  registries: Registries;
  theme: Theme;
}) {
  const tags = listOf(entity.frontMatter['tags']);
  const markers = listOf(entity.frontMatter['markers']);
  if (!tags.length && !markers.length) return null;

  return (
    <div className="chips">
      {tags.map((tag) => {
        const hue = tagTint(registries, tag);
        const style = hue ? chipTint(theme, hue) : null;
        const folder = registries.folders.byName.get(tag.trim().toLowerCase());
        return (
          <FolderLink
            key={tag}
            name={tag}
            folders={registries.folders}
            className="chip t-tagLabel"
            style={style ? { background: style.fill, borderColor: style.border } : undefined}
          >
            {folder ? (
              <span className="chip-mark">
                <TagMarkGlyph mark={folderMark(folder, theme)} size={11} named />
              </span>
            ) : (
              hue && <span className="chip-dot" style={{ background: markInk(theme, hue) }} />
            )}
            {tag}
          </FolderLink>
        );
      })}
      {markers.map((marker) => (
        <span
          key={marker}
          className="chip marker t-tagLabel"
          title={registries.markers.get(marker.toLowerCase())?.scope}
        >
          ⌇ {marker}
        </span>
      ))}
    </div>
  );
}

function FileFacts({ entity, can }: { entity: VaultEntity; can: Capabilities }) {
  const rows: [string, string][] = [['path', entity.path]];
  if (can.hasTranscript) rows.push(['audio', 'referenced · not in the vault']);
  if (can.ordered && can.planned) {
    rows.push(['order', `${flat(entity.frontMatter['status'])} · ${flat(entity.frontMatter['order'])}`]);
  }
  if (entity.frontMatter['updated']) rows.push(['updated', flat(entity.frontMatter['updated'])]);
  if (can.planned) {
    rows.push(['evidence', flat(entity.frontMatter['evidence']) || '— (needed for done)']);
  }

  return (
    <RailSection title="file">
      <dl className="file-facts">
        {rows.map(([k, v]) => (
          <div key={k} className="file-row">
            <dt className="t-metaMono">{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="t-metaMono file-locked">machine state — never edited here</p>
    </RailSection>
  );
}

/** A file that declares a status has one. Nothing else does, and a default
 *  here would invent a state the file never claimed. */
/**
 * The state an arc declares, or null for anything that declares none — a
 * recording has no status and must not get a placeholder glyph.
 *
 * Read off STATUSES rather than listed by hand: written out, this missed
 * `needs-input` the day it was added, and silently, because a missing glyph
 * looks like a file that never set one.
 */
function statusOf(entity: VaultEntity): Status | null {
  const declared = flat(entity.frontMatter['status']);
  return STATUSES.includes(declared as Status) ? (declared as Status) : null;
}
